import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Question } from '@estercare/ester-shared'
import { useScreeningQuestionnaireStore } from '../../state/store'
import {
  useFetchNextQuestionMutation,
  useGetPreviousQuestionQuery,
  usePostAnswerAndGetNextQuestionMutation,
} from '../../api/ester-b2b-api/private/screening/screeningQueries'
import { useScreeningAnswerValidation } from '../../hooks/useScreeningAnswerValidation'
import { useDelayedLoading } from '../../hooks/useDelayedLoading'
import { queryErrorHandlerWithMessage } from '../../utils/queryErrorHandler'

// The backend signals a completed questionnaire with this sentinel question id
const END_OF_QUESTIONNAIRE_ID = 'EndOfQuestionnaire'

// Optional questions must still be answered to advance, so skipping posts an explicit blank
const BLANK_ANSWER = ['blank']

const SKELETON_DELAY_MS = 700

type ScreeningQuestionnaireState = {
  currentQuestion: Question | null
  isLoading: boolean
  showSkeleton: boolean
  isAnswerValid: boolean
  showValidation: boolean
  canGoBack: boolean
  errorMessage?: string
  dismissError: () => void
  goToNextQuestion: () => void
  goToPreviousQuestion: () => Promise<void>
}

/**
 * Drives one screening questionnaire: fetches the current question, posts answers, steps
 * backwards, and navigates to `submitPath` once the questionnaire reports it is finished.
 *
 * The question itself lives in the questionnaire store, since the question components update
 * the in-progress answer from far down the tree.
 *
 * Each posted answer is recorded in that store too, since the questionnaire API offers no way to
 * read one back and the steps after the last question need them.
 */
export const useScreeningQuestionnaire = (
  screeningId: string | undefined,
  submitPath: string
): ScreeningQuestionnaireState => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { currentQuestion, setCurrentQuestion, recordAnswer } = useScreeningQuestionnaireStore()
  const { refetch: getPreviousQuestion, isLoading: isPrevQuestionPending } = useGetPreviousQuestionQuery(screeningId)
  const { mutate: getNextQuestion, isPending: isNextQuestionPending } = useFetchNextQuestionMutation()
  const { mutate: postAnswerAndGetNextQuestion, isPending: isPostAnswerPending } =
    usePostAnswerAndGetNextQuestionMutation()

  const [errorMessage, setErrorMessage] = useState<string>()

  const isLoading = isPostAnswerPending || isPrevQuestionPending || isNextQuestionPending
  const showSkeleton = useDelayedLoading(isLoading, SKELETON_DELAY_MS)

  const { isValid: isAnswerValid, hasAnswer } = useScreeningAnswerValidation(currentQuestion)

  const showError = useCallback(
    (context: string) => (error: Error) => setErrorMessage(queryErrorHandlerWithMessage(context, t)(error)),
    [t]
  )

  useEffect(() => {
    if (screeningId && currentQuestion?.id === END_OF_QUESTIONNAIRE_ID) {
      navigate(submitPath)
    }
  }, [screeningId, currentQuestion?.id, navigate, submitPath])

  useEffect(() => {
    if (!screeningId) return
    getNextQuestion(screeningId, {
      onSuccess: setCurrentQuestion,
      onError: showError('fetch next question'),
    })
  }, [screeningId, getNextQuestion, setCurrentQuestion, showError])

  const goToPreviousQuestion = useCallback(async () => {
    const { data: previousQuestion, error } = await getPreviousQuestion()

    if (previousQuestion) {
      setCurrentQuestion(previousQuestion)
      return
    }

    if (error) {
      showError('get previous question')(error)
      return
    }

    setErrorMessage(t('employee.screening.previousQuestionError'))
  }, [getPreviousQuestion, setCurrentQuestion, showError, t])

  const goToNextQuestion = useCallback(() => {
    if (!currentQuestion || !screeningId) {
      setErrorMessage(t('employee.screening.nextQuestionError'))
      return
    }

    const answer = isAnswerValid && currentQuestion.answer ? currentQuestion.answer : BLANK_ANSWER

    postAnswerAndGetNextQuestion(
      { screeningId, questionId: currentQuestion.id, answer },
      {
        onSuccess: (nextQuestion) => {
          recordAnswer(currentQuestion.id, answer)
          setCurrentQuestion(nextQuestion)
        },
        onError: showError('post answer and get next question'),
      }
    )
  }, [
    currentQuestion,
    isAnswerValid,
    postAnswerAndGetNextQuestion,
    recordAnswer,
    screeningId,
    setCurrentQuestion,
    showError,
    t,
  ])

  const dismissError = useCallback(() => setErrorMessage(undefined), [])

  return {
    currentQuestion,
    isLoading,
    showSkeleton,
    isAnswerValid,
    showValidation: !isAnswerValid && hasAnswer,
    canGoBack: !currentQuestion?.metadata?.isFirstQuestion,
    errorMessage,
    dismissError,
    goToNextQuestion,
    goToPreviousQuestion,
  }
}
